import { useNavigate } from 'react-router'
import { AlertCircle } from 'lucide-react'
import { useProfileStatus } from '@/api/onboarding'
import { useRegionalCopy } from '@/lib/regional-copy'

/**
 * Persistent, non-dismissable nudge for owners whose clinic is still an
 * unconfigured placeholder (CLA-711).
 *
 * Per ADR-013 (capability gating by documentation) this gates on **identity
 * only** — it never blocks core use, and capability documents (indemnity,
 * AHPRA) are surfaced inside the flow as "unlocks", not here. Tone is "finish
 * setting up", never "you did this wrong".
 *
 * Mounted in AppShell's chrome branch, so it only ever renders on
 * non-onboarding pages; every /onboarding/* path (including
 * /onboarding/complete-profile) renders bare and hides it automatically.
 */
export function ClinicProfileBanner() {
  const navigate = useNavigate()
  const { data, isLoading } = useProfileStatus()
  const t = useRegionalCopy()

  // Strict gate: no flash before the query resolves, and nothing once the
  // clinic is set up.
  if (isLoading || !data || data.complete) return null

  return (
    <div className="bg-warning-light border-b border-warning/25 px-4 py-2 flex items-center gap-3 shrink-0">
      <AlertCircle className="w-4 h-4 text-warning shrink-0" aria-hidden />
      <p className="flex-1 text-[13px] text-on-warning-soft">
        <span className="font-semibold">
          Finish setting up {data.clinic_name || `your ${t('clinic')}`}.
        </span>{' '}
        Add a few details so everything in Claro is ready for you.
      </p>
      <button
        onClick={() => navigate('/onboarding/complete-profile')}
        className="shrink-0 px-3 py-1.5 text-xs font-semibold text-white bg-claro-600 rounded-lg hover:bg-claro-700 transition-colors"
      >
        Finish setup
      </button>
    </div>
  )
}
